import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import { loadConfigs } from '../../actions/Login'
import Auth0Session from './Auth0Session'
import { GlobalLoadingIndicator } from '../common/GlobalLoadingIndicator'

function InvitationAcceptor({ invitation, organization }) {
  const { loginWithRedirect, isLoading } = useAuth0()

  useEffect(() => {
    if (isLoading) return
    loginWithRedirect({ authorizationParams: { invitation, organization } })
  }, [isLoading, loginWithRedirect, invitation, organization])

  return <GlobalLoadingIndicator isLoading />
}

InvitationAcceptor.propTypes = {
  invitation: PropTypes.string.isRequired,
  organization: PropTypes.string.isRequired
}

// Loads /configs, then — only when an Auth0 domain is configured — wraps the
// app in Auth0Provider + the Auth0Session token-sync bridge. No domain (Auth0
// not configured), renders children with no provider so the local TestOAuthLogin
// path is completely untouched. Lives inside BrowserRouter so onRedirectCallback
// can use the router to restore the pre-login location. PD-27768, PD-30771.
export default function Auth0Bootstrap({ children }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const config = useSelector((state) => state.login.oauthConfig)

  useEffect(() => { dispatch(loadConfigs()) }, [dispatch])

  if (!config.loaded) return <GlobalLoadingIndicator isLoading />

  if (!config.auth0Domain) return children

  const urlParams = new URLSearchParams(window.location.search)
  const invitation = urlParams.get('invitation')
  const organization = urlParams.get('organization')

  return (
    <Auth0Provider
      domain={config.auth0Domain}
      clientId={config.auth0ClientId}
      authorizationParams={{ audience: config.auth0Audience, redirect_uri: window.location.origin }}
      onRedirectCallback={(appState) => navigate(appState?.returnTo || '/')}
      useRefreshTokens
      cacheLocation="localstorage"
    >
      {invitation && organization
        ? <InvitationAcceptor invitation={invitation} organization={organization} />
        : <><Auth0Session />{children}</>}
    </Auth0Provider>
  )
}

Auth0Bootstrap.propTypes = {
  children: PropTypes.node
}
